import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import "./Header";
import {
  IonApp,
  IonAvatar,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonTitle,
  IonToolbar,
  isPlatform,
  useIonActionSheet,
  useIonAlert,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillEnter,
} from "@ionic/react";

import { PostHogProvider, usePostHog } from "posthog-js/react";
import { posthog } from "posthog-js";

import { BookingOverviewApiHandler } from "./pages/BookingOverviewPage";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, useHistory, useLocation } from "react-router-dom";
import { LoginPageApiHandler } from "./pages/LoginPage/LoginPageApiHandler";

import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { Routes, TabGroup } from "./utility/Routes";

import { VenuePageApiHandler } from "@greeter-venue/pages/VenuePage/VenuePageApiHandler";
import { ProductCreatedPageRouteHandler } from "./pages/ProductCreatedPage/ProductCreatedPageRouteHandler";
import { EditVenuePageApiHandler } from "./pages/EditVenuePage/EditVenuePageApiHandler";
import { GoogleMapsProvider } from "./contexts/GoogleMapsProvider";
import { App as CapApp, URLOpenListenerEvent } from "@capacitor/app";
import { EditEventPageApiHandler } from "./pages/EditEventPage/EditEventPageApiHandlerV2";
import { EditEventCompletedApiHandler } from "./pages/EventEditCompletedPage/EditEventCompletedApiHandler";
import { TicketScannerPageApiHandler } from "./pages/TicketScannerPage";
import { useVenueQuery, useVenuesQuery } from "./api/Hooks";
import { calendar, grid, reader, storefront, wine } from "ionicons/icons";
import {
  AuthProvider,
  FloatingActionBar,
  FloatingActionSubTitle,
  FloatingActionTitle,
  GreeterVenueApiProvider,
  LazyCoverImage,
  LoadingListener,
  useAuth,
  useGreeterVenueApi,
  useImage,
} from "@greeter/matter";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import defaultUser from "./assets/defaultUser.png";
import { EditBookingPageApiHandler } from "./pages/EditBookingPage";
import { getConfig, getEnv, getFirebaseConfig } from "./utility/ConfigHooks";
import { bookingKeys } from "./api/QueryKeys";
import { DatePeriod } from "@greeter/date";
import { TableOverviewPageApiHandler } from "./pages/TableOverviewPage/TableOverviewPageApiHandler";
import { TableServiceOverviewApiHandler } from "./pages/TableServiceOverviewPage";
import { Log, logger } from "@greeter/log";
import { useLoading } from "@greeter/loading";
import { EditMenuPageApiHandler } from "./pages/EditMenuPage/EditMenuPageApiHandler";
import { EditBundleCompletedApiHandler } from "./pages/EditBundleCompletedPage";
import { EditBundlePageApiHandler } from "./pages/EditBundlePage/EditBundlePageApiHandler";
import { LocalStorageVenueApi } from "./pages/EditBundlePage/LocalStorageApi";
import { FeatureFlag, useLocalFeatureFlag } from "@greeter/local-feature-flags";
import { ProductPageRouteHandler } from "./pages/ProductPage";
import { createAuth } from "@greeter/auth";
import { VenueApi } from "@greeter/venue-api";
import { EditTicketEventPageApiHandler } from "./pages/EditTicketEventPage/EditTicketEventPageApiHandler";
import {
  FeatureFlagsProvider,
  useFeatureFlags,
} from "./contexts/FeatureFlagsProvider";
import { TicketEventOverviewPageApiHandler } from "./pages/TicketEventOverviewPage/TicketEventOverviewPageApiHandler";
import {
  IconHanger,
  IconClipboardList,
  IconDoorExit,
  IconQrcode,
} from "@tabler/icons-react";
import { TicketOverviewPageApiHandler } from "./pages/TicketOverviewPage";

import { WardrobeOverviewPageApiHandler } from "./pages/WardrobeOverviewPage/WardrobeOverviewPageApiHandler";

async function attempt(fn: () => Promise<void> | void) {
  try {
    await fn();
  } catch {
    // Ignore
  }
}

// posthog.init('phc_6OUeu1GfKjIxHDdpALF280hTbD5ixWEAB75rtPqk28B', { api_host: 'https://eu.posthog.com' });

const queryClient = new QueryClient();

export function createVenueApi() {
  const env = getEnv();
  const config = getConfig();
  const firebaseConfig = getFirebaseConfig();

  const auth = createAuth({ env, firebaseConfig });

  return new VenueApi(config, auth);
}

const venueApi = createVenueApi();

const pushLog = logger("[PushNotification]");
const pushDebug = logger("[PushNotification]");

function usePushNotifications() {
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();
  const { api: venueApi } = useGreeterVenueApi();
  useEffect(() => {
    (async () => {
      if (Capacitor.isNativePlatform() && isLoggedIn) {
        try {
          pushDebug("Initiating PushNotifications registration");
          PushNotifications.addListener(
            "registrationError",
            (error: any) => {},
          );
          PushNotifications.addListener("registration", async (token) => {
            pushDebug(`Subscribing to notifications with token ${token.value}`);
            await venueApi.subscribeToNotifications(token.value);
          });

          PushNotifications.addListener(
            "pushNotificationReceived",
            async (notification) => {
              // Invalidate all bookings and refetch, since the notification means
              // that we have a new booking
              queryClient.invalidateQueries({
                queryKey: bookingKeys.all(
                  new DatePeriod({
                    from: new Date(),
                    to: new Date(Number.MAX_VALUE),
                  }),
                ),
              });
            },
          );

          const permissionState = (await PushNotifications.checkPermissions())
            .receive;
          if (permissionState !== "granted") {
            PushNotifications.requestPermissions().then((result) => {
              if (result.receive === "granted") {
                PushNotifications.register();
              }
            });
          } else if (permissionState === "granted") {
            PushNotifications.register();
          }
        } catch (e) {
          pushDebug("Unable to register for push notification.", e);
        }
      }
    })();
  }, [venueApi, isLoggedIn, queryClient]);
}

function useTapCounter(
  triggerAt: number,
  staleTimeout: number,
  onTrigger: () => void,
) {
  const [clicks, setClicks] = useState(0);

  useEffect(() => {
    if (clicks !== 0) {
      let id = setTimeout(() => setClicks(0), staleTimeout);
      return () => {
        clearTimeout(id);
      };
    }
  }, [clicks]);

  useEffect(() => {
    if (clicks >= triggerAt) {
      onTrigger();
      setClicks(0);
    }
  }, [clicks, onTrigger]);

  return () => setClicks((c) => c + 1);
}

function DevMenu() {
  const [present] = useIonActionSheet();
  const { auth } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  const history = useHistory();

  const trigger = useTapCounter(5, 250, () => {
    present([
      {
        text: "(Beta) Menu editor",
        handler() {
          history.push(Routes.mainGroup.editMenu.route());
        },
      },
      {
        text: "Log ud",
        async handler() {
          await auth?.logout();
          queryClient.cancelQueries();
          queryClient.clear();
          PushNotifications.removeAllListeners();
          window.location.reload();
        },
      },
    ]);
  });

  useEffect(() => {
    const handleClick = () => {
      if (location.pathname !== Routes.mainGroup.venueTab.root.route()) return;
      trigger();
    };
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [location.pathname]);

  return null;
}

function PushNotificationsListener() {
  usePushNotifications();
  return null;
}

function AppUrlListener() {
  const history = useHistory();
  useEffect(() => {
    CapApp.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      // Example url: https://beerswift.app/tabs/tab2
      // slug = /tabs/tab2
      const slug = event.url.split(".venue").pop();
      if (slug) {
        history.push(slug);
      }

      // If no match, do nothing - let regular routing
      // logic take over
    });
  }, [history]);

  return null;
}

/**
 * Identifies the logged in user by ID and email
 */
export function PosthogIdentifier(props: PropsWithChildren) {
  const posthog = usePostHog();
  const login = useAuth();
  const venue = useVenueQuery();

  useEffect(() => {
    if (venue.data) {
      posthog.group("venue", venue.data.name);
    }
    posthog.identify(login.userInfo?.id, { email: login.userInfo?.email });
  }, [login, posthog]);

  return props.children;
}

// const AppUpdateListener: React.FC = () => {
//   useEffect(() => {
//     if (Capacitor.isNativePlatform() && isPlatform("android")) {
//       const handle = AppUpdate.addListener(
//         "onFlexibleUpdateStateChange",
//         (s) => {
//           if (s.installStatus === FlexibleUpdateInstallStatus.INSTALLED) {
//             AppUpdate.completeFlexibleUpdate();
//           }
//         }
//       );
//       AppUpdate.startFlexibleUpdate();

//       return () => {
//         handle.remove();
//       };
//     }
//   }, []);

//   return null;
// };

const localStorageMockApi = new LocalStorageVenueApi();

type OnlineStatus = "online" | "offline";
export function useNetworkStatus() {
  const [status, setStatus] = useState<OnlineStatus>("offline");

  useEffect(() => {
    function updateStatus(s: OnlineStatus) {
      return () => setStatus(s);
    }

    const offline = updateStatus("offline");
    const online = updateStatus("online");

    window.navigator.onLine ? online() : offline();

    window.addEventListener("online", online);
    window.addEventListener("offline", offline);

    return () => {
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", online);
    };
  }, []);

  return status;
}

function Router() {
  const { isLoggedIn, userInfo } = useAuth();
  return (
    <IonReactRouter key={!isLoggedIn ? "not logged in" : userInfo?.id}>
      <AppUrlListener />
      <DevMenu />
      <AuthRouter />
    </IonReactRouter>
  );
}

export function App() {
  const env = getEnv();
  const config = getConfig();
  const firebaseConfig = getFirebaseConfig();
  const status = useNetworkStatus();

  return (
    <IonApp>
      <PostHogProvider client={posthog}>
        <GreeterVenueApiProvider api={venueApi}>
          {/* {Capacitor.isNativePlatform() && isPlatform("android") && <AppUpdateListener />} */}
          <QueryClientProvider client={queryClient}>
            <AuthProvider
              env={env}
              config={config}
              firebaseConfig={firebaseConfig}
            >
              <FeatureFlagsProvider>
                {/*<PosthogIdentifier />*/}
                <PushNotificationsListener />
                <GoogleMapsProvider>
                  <Router />
                </GoogleMapsProvider>
              </FeatureFlagsProvider>
            </AuthProvider>
          </QueryClientProvider>
        </GreeterVenueApiProvider>
        <LoadingListener />
        {status === "offline" && (
          <FloatingActionBar
            style={{ bottom: "calc(0.5rem + 50px)" }}
            type="danger"
          >
            <FloatingActionTitle>Hov! Du er ikke online...</FloatingActionTitle>
            <FloatingActionSubTitle>
              Du skal have internet før du kan bruge appen
            </FloatingActionSubTitle>
          </FloatingActionBar>
        )}
      </PostHogProvider>
    </IonApp>
  );
}

function MainTabs() {
  const { api } = useGreeterVenueApi();
  const featureFlags = useFeatureFlags();
  const { isLoggedIn, userInfo } = useAuth();
  const { waitFor } = useLoading();

  const [showVenuePicker, setShowVenuePicker] = useState(false);
  const trigger = useTapCounter(2, 500, () => {
    setShowVenuePicker(true);
  });
  const modalRef = useRef<HTMLIonModalElement>(null);

  const queryClient = useQueryClient();

  const venueQuery = useVenueQuery({ enabled: isLoggedIn });
  const venuesQuery = useVenuesQuery({ enabled: isLoggedIn });
  const venueImage = useImage(venueQuery.data?.logoUrl ?? "", defaultUser);

  const router = useIonRouter();

  useIonViewWillEnter(() => {
    if (userInfo?.role === "Doorman")
      router.push(Routes.ticketsGroup.ticketsTab.root.route());
  }, [router, userInfo]);

  return (
    <IonTabs>
      <IonRouterOutlet>
        <Route exact path="/">
          <Redirect to={Routes.mainGroup.bookingOverview.route()} />
        </Route>
        <Route exact path={Routes.mainGroup.tableServiceOverview.template()}>
          <TableServiceOverviewApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.bookingOverview.template()}>
          <BookingOverviewApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.editBooking.template()}>
          <EditBookingPageApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.venueTab.root.template()}>
          <VenuePageApiHandler />
        </Route>

        <Route exact path={Routes.mainGroup.venueTab.product.template()}>
          <ProductPageRouteHandler />
        </Route>

        <Route exact path={Routes.mainGroup.productCreated.template()}>
          <ProductCreatedPageRouteHandler />
        </Route>
        <Route exact path={Routes.mainGroup.editVenue.template()}>
          <EditVenuePageApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.editEvent.template()}>
          <EditEventPageApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.ticketEvents.template()}>
          <TicketEventOverviewPageApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.editTicketEvent.template()}>
          <EditTicketEventPageApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.editEventCompleted.template()}>
          <EditEventCompletedApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.tableOverview.template()}>
          <TableOverviewPageApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.editMenu.template()}>
          <EditMenuPageApiHandler />
        </Route>

        <Route exact path={Routes.mainGroup.venueTab.editBundle.template()}>
          <EditBundlePageApiHandler />
        </Route>
        <Route
          exact
          path={Routes.mainGroup.venueTab.editBundleCompleted.template()}
        >
          <EditBundleCompletedApiHandler />
        </Route>
        <Route exact path={Routes.mainGroup.venueTab.wardrobe.template()}>
          <WardrobeOverviewPageApiHandler showBackButton />
        </Route>
        <Route exact path={Routes.mainGroup.wardrobe.template()}>
          <WardrobeOverviewPageApiHandler />
        </Route>
      </IonRouterOutlet>
      <IonTabBar
        style={{
          "--background": "var(--gm-color-alt-bg)",
          "--color-selected": "var(--gm-color-primary-highlight)",
          "--color": "var(--gm-color-default-bg)",
        }}
        slot="bottom"
      >
        {/*isPlatform("tablet") && (
          <IonTabButton
            data-test-id="tab-tableserviceoverview"
            tab={Routes.mainGroup.tableServiceOverview.route()}
            href={Routes.mainGroup.tableServiceOverview.route()}
          >
            <IonIcon icon={wine} />
            <IonLabel>Tableservice</IonLabel>
          </IonTabButton>
        )*/}
        {(isPlatform("tablet") || isPlatform("desktop")) &&
          featureFlags["wardrobe.v1.venue"] && (
            <IonTabButton
              data-testid="tab-wardrobe"
              tab={Routes.mainGroup.wardrobe.route()}
              href={Routes.mainGroup.wardrobe.route()}
            >
              <IconHanger />
              <IonLabel>Garderobe</IonLabel>
            </IonTabButton>
          )}
        <IonTabButton
          data-test-id="tab-tableoverview"
          tab={Routes.mainGroup.tableOverview.route()}
          href={Routes.mainGroup.tableOverview.route()}
        >
          <IonLabel>Bordoversigt</IonLabel>
          <IonIcon icon={grid} />
        </IonTabButton>
        <IonTabButton
          data-testid="tab-bookingoverview"
          tab={Routes.mainGroup.bookingOverview.route()}
          href={Routes.mainGroup.bookingOverview.route()}
        >
          <IonLabel>Bookings</IonLabel>
          <IonIcon icon={reader} />
        </IonTabButton>

        {featureFlags["ticket-events.v1.venue"] && (
          <IonTabButton
            data-testid="tab-ticketevents"
            tab={Routes.mainGroup.ticketEvents.route()}
            href={Routes.mainGroup.ticketEvents.route()}
          >
            <IonLabel>Billet Events</IonLabel>
            <IonIcon icon={calendar} />
          </IonTabButton>
        )}
        <IonTabButton
          tab={Routes.mainGroup.venueTab.root.route()}
          href={Routes.mainGroup.venueTab.root.route()}
          data-testid="tab-venue"
          onClick={(ev) => {
            console.debug(ev);
            trigger();
          }}
        >
          {!venueQuery.isLoading && venueQuery.data ? (
            <div
              style={{
                height: "100%",
                width: "auto",
                aspectRatio: "1 / 1",
                borderRadius: "100vw",
                overflow: "hidden",
                marginTop: "0.5rem",
                marginBottom: "0.5rem",
              }}
            >
              <LazyCoverImage src={venueImage.src} />
            </div>
          ) : (
            <>
              <IonIcon icon={storefront} />
              <IonLabel>Venue</IonLabel>
            </>
          )}
        </IonTabButton>
      </IonTabBar>

      <IonModal
        ref={modalRef}
        isOpen={showVenuePicker}
        onDidDismiss={() => {
          setShowVenuePicker(false);
        }}
      >
        <IonHeader>
          <IonToolbar
            style={{ "--background-color": "var(--gm-color-default-bg)" }}
          >
            <IonTitle slot="start">Vælg venue</IonTitle>
            <p
              slot="end"
              style={{ paddingRight: "1rem" }}
              onClick={() => {
                setShowVenuePicker(false);
              }}
            >
              X
            </p>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding" style={{ height: "50%" }}>
          <IonList>
            {venuesQuery.data?.map((v) => {
              return (
                <IonItem
                  key={v.id}
                  onClick={async () => {
                    modalRef.current?.dismiss();
                    setShowVenuePicker(false);

                    await waitFor(
                      api.selectCurrentVenue(v.id),
                      "Skifter venue...",
                    );
                    queryClient.removeQueries();
                    await waitFor(
                      queryClient.invalidateQueries(),
                      "Skifter venue...",
                    );
                    await waitFor(
                      queryClient.refetchQueries(),
                      "Skifter venue...",
                    );
                  }}
                >
                  <IonAvatar slot="start">
                    <IonImg src={v.logoUrl} />
                  </IonAvatar>
                  <IonLabel>
                    <h2>{v.name}</h2>
                    <p>
                      {v.address.city}, {v.address.street}
                    </p>
                  </IonLabel>
                </IonItem>
              );
            })}
          </IonList>
        </IonContent>
      </IonModal>
    </IonTabs>
  );
}

function TicketsTabs() {
  const { auth, userInfo } = useAuth();
  const [presentAlert] = useIonAlert();
  const queryClient = useQueryClient();
  const confirmLogout = () =>
    presentAlert({
      header: "Log ud?",
      message: "Er du sikker på, at du vil logge ud?",
      buttons: [
        { text: "Annuller", role: "cancel" },
        {
          text: "Log ud",
          handler: async () => {
            attempt(async () => await auth?.logout());
            attempt(async () => await queryClient.cancelQueries());
            attempt(() => queryClient.clear());
            attempt(async () => await PushNotifications.removeAllListeners());
            attempt(() => window.location.reload());
          },
        },
      ],
    });
  return (
    <IonTabs>
      <IonRouterOutlet>
        <Route exact path={Routes.ticketsGroup.scannerTab.root.template()}>
          <TicketScannerPageApiHandler />
        </Route>
        <Route exact path={Routes.ticketsGroup.ticketsTab.root.template()}>
          <TicketOverviewPageApiHandler />
        </Route>
      </IonRouterOutlet>
      <IonTabBar
        style={{
          "--background": "#0F131A",
          "--color-selected": "#2ECC71",
          "--color": "#94A3B8",
        }}
        slot="bottom"
      >
        <IonTabButton
          tab={Routes.ticketsGroup.scannerTab.root.route()}
          href={Routes.ticketsGroup.scannerTab.root.route()}
        >
          <IconQrcode />
          <IonLabel>Scan</IonLabel>
        </IonTabButton>
        <IonTabButton
          tab={Routes.ticketsGroup.ticketsTab.root.route()}
          href={Routes.ticketsGroup.ticketsTab.root.route()}
        >
          <IconClipboardList />
          <IonLabel>Gæsteliste</IonLabel>
        </IonTabButton>
        {userInfo?.role === "VenueAdmin" && (
          <IonTabButton
            tab={Routes.mainGroup.ticketEvents.route()}
            href={Routes.mainGroup.ticketEvents.route()}
          >
            <IconDoorExit />
            <IonLabel>Exit</IonLabel>
          </IonTabButton>
        )}

        {userInfo?.role === "Doorman" && (
          // The escape hatch for Doorman
          <IonTabButton onPointerDown={confirmLogout}>
            <IconDoorExit aria-hidden="true" />
            <span>Exit</span>
          </IonTabButton>
        )}
      </IonTabBar>
    </IonTabs>
  );
}

function AuthRouter() {
  const { isLoggedIn, userInfo } = useAuth();

  return isLoggedIn ? (
    <IonRouterOutlet key={`${userInfo?.id}${userInfo?.role}`}>
      <Redirect exact path="/" to={Routes.mainGroup.bookingOverview.route()} />
      <Route path={"/" + TabGroup.MAIN} component={MainTabs} />
      <Route path={"/" + TabGroup.TICKETS} component={TicketsTabs} />
    </IonRouterOutlet>
  ) : (
    <LoginPageApiHandler />
  );
}
